'use client'

import avatarIcon from '@public/images/avatar.svg'
import cartIcon from '@public/images/cart.svg'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { Stamp } from '@src/components/TopNavV4/Stamp'
import { useTopNav } from '@src/components/TopNavV4/TopNavContext'
import { configPublic } from '@src/config.public'
import { useCart } from '@src/hooks/useCart'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { getCartQuantity } from '@src/utils/cart.util'
import { cn } from '@src/utils/tailwind'

import type { TopNavItem } from '../TopNavProps'

const itemClasses =
  'flex items-center cursor-pointer h-full transition-all hover:text-bodi-white text-body-md! text-nowrap'

const hoverTimeouts: Record<string, number> = {}
const hoverDelay = configPublic.general.topNavDelay

export const TopNavDesktopBar = () => {
  const { t } = useLocale('common')
  const { cart } = useCart()
  const quantity = getCartQuantity(cart)
  const { user, getLoginHref } = useSession()
  const loginHref = getLoginHref()
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [accountContainerWidth, setAccountContainerWidth] = useState<number | undefined>(
    getInitialContainerWidth()
  )
  const trackSelectNavCta = useTrackSelectNavCta()
  const {
    logo,
    items,
    account,
    isExpanded,
    expandedMenu,
    setExpandedMenu,
    setExpanded,
    setAccountExpanded,
  } = useTopNav()
  const accountContainerRef = useRef<HTMLDivElement>(null)
  function getInitialContainerWidth() {
    if (!user) return 302 // width when user is not logged in
    if (user?.hasBodiAccess) return 157 // width for users with Bodi access
    if (!user?.hasBodiAccess) return 300 // width for users without Bodi access
  }

  useEffect(() => {
    function updateWidth() {
      if (accountContainerRef.current) {
        setAccountContainerWidth(accountContainerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const renderItems = (items?: TopNavItem[]) => {
    return items
      ?.filter((item) => Boolean(item.title))
      .map((item, index) => {
        const isSelected = item.title === expandedMenu?.title && isExpanded
        const componentKey = String(item.title)

        if (!item?.hasSubItems && !item?.hasMiniMenu) {
          return (
            <LocalizedLink
              data-testid={`nav-item-${index}`}
              onMouseEnter={() => setExpanded(false)}
              onClick={() => {
                trackSelectNavCta({ 'cta url': item.href })
              }}
              key={componentKey}
              className={cn(itemClasses, 'group relative')}
              href={item.href ?? ''}
              rel={item.newTab ? 'noopener noreferrer' : ''}
              target={item.newTab ? '_blank' : '_self'}
            >
              {item.title}
              <Stamp text={item.stamp} />
              <div
                className={
                  'absolute bottom-0 left-0 h-[3px] w-full bg-bodi-green-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100'
                }
              ></div>
            </LocalizedLink>
          )
        }

        if (item?.hasMiniMenu) {
          return (
            <div
              key={componentKey}
              className={cn(
                'group relative flex h-full items-center transition-all hover:text-bodi-white'
              )}
              onMouseEnter={() => {
                setActiveIndex(index)
                setExpanded(false)
              }}
              onMouseLeave={() => {
                setActiveIndex(null)
              }}
            >
              <LocalizedLink
                className={cn(
                  itemClasses,
                  'relative flex appearance-none items-center gap-2 font-normal'
                )}
                href={item.href ?? ''}
              >
                {item.title}
                <Stamp text={item.stamp} />
                <div
                  className={
                    'absolute bottom-0 left-0 z-max h-[3px] w-full bg-bodi-green-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100'
                  }
                />
              </LocalizedLink>
              {item.hasMiniMenu && activeIndex === index && (
                <div className="absolute top-full left-1/2 z-10 -translate-x-1/2 bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 py-5 whitespace-nowrap">
                  <ul>
                    {item.miniMenuSubItems?.map((subItem, index) => (
                      <li className="flex flex-col px-6 py-2" key={`${subItem.title}-${index}`}>
                        <div className="flex items-center">
                          <Link
                            href={subItem.href ?? ''}
                            className="text-body-base whitespace-nowrap text-bodi-gray-butters hover:text-bodi-white hover:underline"
                            dangerouslySetInnerHTML={{ __html: subItem.title ?? '' }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )
        }

        return (
          <button
            data-testid={`nav-item-${index}`}
            key={componentKey}
            onMouseEnter={() => {
              setExpandedMenu(item)
              hoverTimeouts[componentKey] = window.setTimeout(() => setExpanded(true), hoverDelay)
            }}
            onMouseLeave={() => {
              clearTimeout(hoverTimeouts[componentKey])
            }}
            onClick={() => {
              clearTimeout(hoverTimeouts[componentKey])
              setExpanded(!isExpanded)
            }}
            className={cn(
              itemClasses,
              'relative flex appearance-none items-center gap-2 font-normal',
              {
                'text-bodi-white font-medium': isSelected,
              }
            )}
          >
            {item.title}
            <Stamp text={item.stamp} />
            <div
              className={cn(
                'absolute bottom-0 left-0 h-[3px] w-full bg-bodi-green-bright-500 transition-opacity',
                {
                  'opacity-100': isSelected,
                  'opacity-0': !isSelected,
                }
              )}
            ></div>
          </button>
        )
      })
  }

  return (
    <div className="relative z-50 h-12 w-full bg-bodi-black-text px-4 font-medium text-bodi-gray-butters">
      <div className="flex h-full items-center justify-between">
        {logo?.src && (
          <div
            className="flex h-full items-center"
            style={accountContainerWidth ? { width: accountContainerWidth } : undefined}
          >
            <LocalizedLink
              onMouseEnter={() => setExpanded(false)}
              onClick={() => {
                trackSelectNavCta({ 'cta url': logo?.href })
              }}
              href={logo?.href ?? ''}
              className="flex h-full shrink-0 items-center"
            >
              <Image
                src={logo.src}
                width={logo?.desktopWidth}
                height={logo?.desktopHeight}
                alt={logo?.altText ?? ''}
              />
            </LocalizedLink>
          </div>
        )}
        <div className="flex h-full items-center gap-6 px-4">{renderItems(items)}</div>
        <div
          onMouseEnter={() => setExpanded(false)}
          className="flex h-full shrink-0 items-center justify-end gap-2"
          ref={accountContainerRef}
        >
          <Link data-testid="cartBtn" href="#cart" className="relative mt-0.5 mr-2" scroll={false}>
            <Image src={cartIcon} width={24} height={24} alt={t('view-cart')} />
            {quantity ? (
              <div
                className={cn(
                  'absolute top-0 left-full -mt-3 ml-[-18px] flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full border-2 border-bodi-black-text bg-bodi-green-bright-500 text-body-xs font-semibold text-bodi-black',
                  { 'px-1.5': quantity > 99 }
                )}
              >
                {quantity > 999 ? '999' : quantity}
              </div>
            ) : null}
          </Link>

          {!user ? (
            <a href={loginHref}>
              <Button
                size="small"
                variant="black"
                className="border border-solid border-bodi-gray-medium"
                onClick={() => {
                  trackSelectNavCta({ 'cta url': loginHref })
                }}
                data-testid="loginBtn"
              >
                {t('login')}
              </Button>
            </a>
          ) : (
            <button
              onClick={() => {
                setAccountExpanded(true)
              }}
              data-testid="accountBtn"
              className="flex items-center gap-2 font-medium text-bodi-gray-butters"
            >
              <Image src={avatarIcon} width={24} height={24} alt="Account icon" />
              {t('my-account')}
            </button>
          )}
          {!user?.hasBodiAccess && account?.bodiCta?.title && account?.bodiCta?.href && (
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': account?.bodiCta?.href })
              }}
              data-testid="bodiBtn"
              href={account?.bodiCta?.href ?? ''}
              rel={account?.bodiCta?.newTab ? 'noopener noreferrer' : ''}
              target={account?.bodiCta?.newTab ? '_blank' : '_self'}
            >
              <Button className="[&>div]:px-8" size="small">
                {account?.bodiCta?.title}
              </Button>
            </LocalizedLink>
          )}
        </div>
      </div>
    </div>
  )
}
